import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Building2,
  CalendarDays,
  FileText,
  GraduationCap,
  Image as ImageIcon,
  ListChecks,
  Megaphone,
  MoreHorizontal,
  Newspaper,
  Paperclip,
  PartyPopper,
  PlayCircle,
  Send,
  Smile,
  Sparkles,
  HelpCircle,
  MessageCircle,
  Star,
  CheckCheck,
  ChevronRight,
  Users,
  X,
} from 'lucide-react';
import './CoolAssistantWidget.css';
import defaultAvatar from '../Logo/Reallybot.png';
import { getApiBaseUrl } from '../config/api';
import { resolveAvatarUrl, handleAvatarError } from '../utils/profilePicUrl';

const API_URL = getApiBaseUrl();

const EVENT_ICONS = [PartyPopper, Megaphone, Star, GraduationCap, ListChecks, CalendarDays];
const EVENT_TONES = ['pink', 'amber', 'blue', 'violet', 'emerald', 'sky'];
const JOB_ICONS = [Briefcase, Building2, GraduationCap, Star];
const JOB_TONES = ['blue', 'emerald', 'violet', 'amber'];
const NEWS_ICONS = [Newspaper, Megaphone, Star];
const GROUP_ICONS = [Users, GraduationCap, Star];

function initialModalPosition() {
  if (typeof window === 'undefined') {
    return { x: 80, y: 80 };
  }
  const w = Math.min(window.innerWidth * 0.92, 420);
  const h = Math.min(window.innerHeight * 0.86, 680);
  return {
    x: 16,
    y: Math.max(16, window.innerHeight - h - 24),
  };
}

function formatTime(ts) {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function coolAppPathFromHref(href) {
  const raw = String(href || '').trim();
  if (!raw) return '';
  if (raw.startsWith('/') && !raw.startsWith('//')) {
    return raw;
  }
  try {
    const url = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const path = `${url.pathname || ''}${url.search || ''}${url.hash || ''}`;
    if (/^\/(news|events|home|group|groups|looking-for-job|hiring|profile|network)\b/i.test(path)) {
      return path;
    }
  } catch {
    return '';
  }
  return '';
}

function formatInlineMarkdown(text) {
  let html = escapeHtml(text);
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(
    /\[([^\]]+)\]\((\/[^\s)]+|https?:\/\/[^\s)]+)\)/g,
    (_, label, href) => {
      const appPath = coolAppPathFromHref(href);
      if (appPath) {
        const safeHref = escapeHtml(appPath);
        return `<a href="${safeHref}" class="cool-assistant-inlink" data-cool-path="${safeHref}">${label}</a>`;
      }
      const safeHref = escapeHtml(href);
      return `<a href="${safeHref}" class="cool-assistant-extlink" target="_blank" rel="noopener noreferrer">${label}</a>`;
    }
  );
  html = html.replace(/\n/g, '<br />');
  return html;
}

function stripCardOverlaps(text) {
  if (!text) return '';
  let out = String(text);
  out = out.replace(/\*\*([^*]+)\*\*/g, '$1');
  out = out.replace(/\[[^\]]+\]\((?:https?:\/\/[^)]+|\/[^)]+)\)/g, '');
  out = out.replace(/https?:\/\/[^\s)]+/gi, '');
  out = out.replace(/open at \/\S+/gi, '');
  out = out.replace(/Upcoming \/ recent Cool Activities:[\s\S]*?(?=\n\n[A-Z]|$)/i, '');
  out = out.replace(/Here is what is currently on the COOL Page:[\s\S]*?(?=\n\n[A-Z]|$)/i, '');
  out = out.replace(/Here are the upcoming Cool Activities:[\s\S]*?(?=If you need more details|$)/i, '');
  out = out.replace(/Hiring posts on the page:[\s\S]*?(?=\n\n[A-Z]|$)/i, '');
  out = out.replace(/Open to work posts:[\s\S]*?(?=\n\n[A-Z]|$)/i, '');
  out = out.replace(/News on the page:[\s\S]*?(?=\n\n[A-Z]|$)/i, '');
  out = out.replace(/Groups on the page:[\s\S]*?(?=\n\n[A-Z]|$)/i, '');
  out = out.replace(/Recent posts on the COOL feed:[\s\S]*?(?=\n\n[A-Z]|$)/i, '');
  out = out.replace(/Here are some recent posts[\s\S]*?(?=\n\nTap |\n\nIf |$)/i, '');
  out = out.replace(/Here are some recent posts on the COOL feed:[\s\S]*$/i, '');
  out = out.replace(/As a guest[\s\S]*?(?=\n\nIf |$)/i, '');
  out = out.replace(/Tap a listing below[\s\S]*$/i, '');
  out = out.replace(/If you'd like to know more[\s\S]*$/i, '');
  out = out.replace(/(?:^|\n)\s*\d+\.\s+[\s\S]+?(?=(?:\n\s*\d+\.\s+)|$)/g, (block) => {
    if (/\(article|\(video|\(photo|\(text|article,|video,/i.test(block)) {
      return '';
    }
    return block;
  });
  out = out.replace(/^(?:[-*]\s+.+\n?)+/gm, (block) => {
    if (/\d{4}|Singapore|location|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|deadline|open at \/|news_id|hiring_id/i.test(block)) {
      return '';
    }
    return block;
  });
  return out.replace(/\n{3,}/g, '\n\n').trim();
}

function normalizeCardGroups(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.filter((group) => group && (group.items?.length || group.heading || group.footer));
  }
  if (raw.items || raw.heading) {
    return [raw];
  }
  return [];
}

function listingIconForItem(type, item, fallbackIcon) {
  const kind = String(item?.kind || item?.post_type || '').toLowerCase();
  if (type === 'posts' || kind) {
    if (kind === 'video') return PlayCircle;
    if (kind === 'photo') return ImageIcon;
    if (kind === 'article') return FileText;
    if (kind === 'hiring') return Briefcase;
    if (kind === 'looking_for_job') return Users;
    if (kind === 'text') return MessageCircle;
  }
  if (type === 'news') return Newspaper;
  if (type === 'jobs') return Briefcase;
  if (type === 'open_to_work') return Users;
  if (type === 'groups') return Users;
  if (type === 'events') return PartyPopper;
  if (type === 'posts') return MessageCircle;
  return fallbackIcon;
}

function listingToneForItem(type, item, fallbackTone) {
  const kind = String(item?.kind || item?.post_type || type || '').toLowerCase();
  if (kind === 'hiring' || type === 'jobs') return 'blue';
  if (kind === 'article') return 'violet';
  if (kind === 'video') return 'emerald';
  if (kind === 'photo') return 'sky';
  if (kind === 'looking_for_job' || type === 'open_to_work') return 'amber';
  if (kind === 'news' || type === 'news') return 'amber';
  if (kind === 'text') return 'sky';
  if (type === 'events') return fallbackTone || 'pink';
  if (type === 'groups') return 'violet';
  return fallbackTone || 'blue';
}

function listingKindLabel(item, type) {
  const kind = String(item?.kind || '').toLowerCase();
  if (type === 'posts' || kind) {
    if (kind === 'article') return 'article';
    if (kind === 'video') return 'video';
    if (kind === 'photo') return 'photo';
    if (kind === 'hiring') return 'hiring';
    if (kind === 'looking_for_job') return 'open to work';
    if (kind === 'text') return 'text';
  }
  if (type === 'news') return 'news';
  if (type === 'jobs') return item.closed ? 'closed' : 'hiring';
  if (type === 'open_to_work') return 'open to work';
  if (type === 'groups') return item.location || 'group';
  if (type === 'events') return 'event';
  return '';
}

function listingMetaLine(item, type) {
  const parts = [];
  const kind = listingKindLabel(item, type);
  if (kind) parts.push(kind);
  if (item.category) parts.push(item.category);
  if (type === 'jobs' && item.company) parts.push(item.company);
  if (item.date) parts.push(item.date);
  else if (item.deadline) parts.push(`deadline ${item.deadline}`);
  if (item.location && type !== 'groups' && type !== 'jobs') parts.push(item.location);
  if (type === 'jobs' && item.location) parts.push(item.location);
  return parts.filter(Boolean).join(', ');
}

function formatHeading(text) {
  const raw = String(text || '');
  const parts = raw.split(/\b(COOL)\b/);
  return parts.map((part, i) =>
    part === 'COOL' ? (
      <strong key={`cool-${i}`} className="cool-assistant-brand">
        COOL
      </strong>
    ) : (
      part
    )
  );
}

function listingIcons(type) {
  if (type === 'jobs' || type === 'open_to_work') {
    return { icons: type === 'open_to_work' ? GROUP_ICONS : JOB_ICONS, tones: JOB_TONES };
  }
  if (type === 'news') {
    return { icons: NEWS_ICONS, tones: ['amber', 'blue', 'violet'] };
  }
  if (type === 'groups') {
    return { icons: GROUP_ICONS, tones: ['violet', 'emerald', 'sky'] };
  }
  if (type === 'posts') {
    return { icons: [Briefcase, FileText, Megaphone, ListChecks, MessageCircle], tones: ['blue', 'violet', 'emerald', 'amber', 'sky'] };
  }
  return { icons: EVENT_ICONS, tones: EVENT_TONES };
}

function MessageText({ text, onOpenPath }) {
  if (!text) return null;
  const onClick = (event) => {
    const link = event.target.closest('a[data-cool-path]');
    if (!link) return;
    event.preventDefault();
    const path = link.getAttribute('data-cool-path');
    if (path && onOpenPath) onOpenPath(path);
  };
  return (
    <div
      className="cool-assistant-richtext"
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(text) }}
    />
  );
}

function ListingCards({ cards, onOpen, isGuest = false }) {
  if (!cards) return null;
  const items = Array.isArray(cards.items) ? cards.items : [];
  if (!items.length && !cards.heading && !cards.footer) return null;
  const type = cards.type || 'events';
  const { icons, tones } = listingIcons(type);
  const heading =
    type === 'posts' && items.length > 1
      ? isGuest
        ? 'As a guest, you can view the following recent posts on the COOL feed:'
        : 'Here are some recent posts on the COOL feed:'
      : cards.heading;
  const footer =
    cards.footer ||
    (type === 'posts'
      ? "If you'd like to know more about any specific post, please let me know!"
      : '');
  return (
    <div className="cool-assistant-feed">
      {heading && (
        <div className="cool-assistant-feed-intro">
          <span className="cool-assistant-feed-sparkle" aria-hidden="true">
            <Sparkles size={16} />
          </span>
          <p className="cool-assistant-event-heading">{formatHeading(heading)}</p>
        </div>
      )}
      {items.length > 0 && (
        <ol className="cool-assistant-event-list">
          {items.map((item, index) => {
            const FallbackIcon = icons[index % icons.length];
            const Icon = listingIconForItem(type, item, FallbackIcon);
            const tone = listingToneForItem(type, item, tones[index % tones.length]);
            const meta = listingMetaLine(item, type);
            const snippet = String(item.snippet || '').replace(/^[\s—–-]+/, '');
            return (
              <li key={item.id || `${item.title}-${index}`}>
                <span className="cool-assistant-index">{index + 1}</span>
                <button
                  type="button"
                  className="cool-assistant-event-row"
                  onClick={() => onOpen(item)}
                >
                  <span className={`cool-assistant-event-icon cool-assistant-event-icon--${tone}`}>
                    <Icon size={16} strokeWidth={2.25} />
                  </span>
                  <span className="cool-assistant-event-copy">
                    <span className="cool-assistant-titleline">
                      <strong>
                        {String(item.title || 'Listing').replace(/^\*+|\*+$/g, '')}
                      </strong>
                      {meta ? <span className="cool-assistant-paren">({meta})</span> : null}
                      {item.closed ? (
                        <span className="cool-assistant-closed">Closed</span>
                      ) : null}
                    </span>
                    {snippet ? (
                      <small className="cool-assistant-snippet">— {snippet}</small>
                    ) : null}
                  </span>
                  <ChevronRight size={16} className="cool-assistant-event-chevron" />
                </button>
              </li>
            );
          })}
        </ol>
      )}
      {footer && (
        <p className="cool-assistant-event-footer">
          <span className="cool-assistant-help-icon" aria-hidden="true">
            <HelpCircle size={13} />
          </span>
          {footer}
        </p>
      )}
    </div>
  );
}

export default function CoolAssistantWidget({ user = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [isBobbing, setIsBobbing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [position, setPosition] = useState(initialModalPosition);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!API_URL) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/assistant_config.php`);
        const raw = await res.text();
        const data = raw ? JSON.parse(raw) : {};
        if (!cancelled && data.success && data.config) {
          setConfig(data.config);
        }
      } catch {
        setConfig({
          enabled: true,
          name: 'COOL Assistant',
          welcome_message:
            "Hi! I'm the COOL Assistant. Ask me anything about this page.",
          avatar_url: '',
          suggested_prompts: [
            'What is COOL?',
            'How do I create a post?',
            'What events are coming up?',
            'How do I find a job here?',
            'What jobs are open right now?',
            'What is the latest news?',
          ],
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const name = config?.name || 'COOL Assistant';
  const avatarSrc = config?.avatar_url || defaultAvatar;
  const welcome = config?.welcome_message || `Hi! I'm ${name}. How can I help?`;
  const suggestions = Array.isArray(config?.suggested_prompts)
    ? config.suggested_prompts
    : [];
  const userAvatar = user ? resolveAvatarUrl(user, API_URL) : null;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, sending, isOpen]);

  useEffect(() => {
    if (!isOpen || !inputRef.current) return undefined;
    const isPhone = window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;
    if (!isPhone) {
      inputRef.current.focus();
    }
    return undefined;
  }, [isOpen]);

  const openChat = () => {
    setHasDragged(false);
    setPosition(initialModalPosition());
    setIsBobbing(true);
    setIsOpen(true);
    setTimeout(() => setIsBobbing(false), 600);
  };

  const onDragStart = (event) => {
    const modal = event.currentTarget.closest('.cool-assistant-modal');
    const rect = modal ? modal.getBoundingClientRect() : null;
    const x = rect ? rect.left : 16;
    const y = rect ? rect.top : 16;
    setPosition({ x, y });
    dragOffsetRef.current = {
      x: event.clientX - x,
      y: event.clientY - y,
    };
    setHasDragged(true);
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return undefined;
    const onMove = (event) => {
      const modalWidth = Math.min(window.innerWidth * 0.92, 420);
      const modalHeight = Math.min(window.innerHeight * 0.86, 680);
      setPosition({
        x: Math.max(0, Math.min(event.clientX - dragOffsetRef.current.x, window.innerWidth - modalWidth)),
        y: Math.max(0, Math.min(event.clientY - dragOffsetRef.current.y, window.innerHeight - modalHeight)),
      });
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  const sendMessage = useCallback(
    async (rawText) => {
      const text = String(rawText || '').trim();
      if (!text || sending) return;
      const history = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const now = Date.now();
      setMessages((prev) => [...prev, { role: 'user', content: text, at: now }]);
      setInput('');
      setSending(true);
      try {
        const res = await fetch(`${API_URL}/assistant_chat.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: text,
            history,
            user_id: user?.id || 0,
            page: location.pathname || '/',
            page_title: typeof document !== 'undefined' ? document.title : '',
          }),
        });
        const raw = await res.text();
        let data = {};
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch {
          data = {};
        }
        const reply =
          data.success && data.reply
            ? data.reply
            : data.message || 'Sorry, I could not answer that right now.';
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: reply,
            cards: data.cards || null,
            at: Date.now(),
          },
        ]);
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'I could not reach the COOL Assistant just now. Please try again.',
            at: Date.now(),
          },
        ]);
      } finally {
        setSending(false);
      }
    },
    [messages, sending, user?.id, location.pathname]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const openListing = (item) => {
    const path = item?.path || '';
    setIsOpen(false);
    if (path) {
      navigate(path);
      return;
    }
    if (item?.id) {
      navigate(`/events/${item.id}`);
    } else {
      navigate('/events');
    }
  };

  const openPath = (path) => {
    if (!path) return;
    setIsOpen(false);
    navigate(path);
  };

  const insertEmoji = () => {
    setInput((prev) => `${prev}🙂`);
    inputRef.current?.focus();
  };

  if (!config || config.enabled === false) {
    return null;
  }

  return (
    <>
      <div className="cool-assistant-wrapper">
        <div className="cool-assistant-bubble">Ask {name.replace(/^Ask\s+/i, '')}</div>
        <button
          type="button"
          className={`cool-assistant-button ${isBobbing ? 'bobbing' : ''}`}
          onClick={openChat}
          aria-label={`Open ${name}`}
        >
          <img src={avatarSrc} alt="" />
        </button>
      </div>

      {isOpen && (
        <div className="cool-assistant-layer">
          <div
            className="cool-assistant-backdrop"
            onClick={() => setIsOpen(false)}
            role="presentation"
          />
          <div
            className="cool-assistant-modal"
            role="dialog"
            aria-modal="true"
            aria-label={name}
            style={
              hasDragged
                ? {
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                    right: 'auto',
                    bottom: 'auto',
                  }
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cool-assistant-header">
              <div
                className="cool-assistant-drag"
                onMouseDown={(event) => {
                  if (window.matchMedia('(max-width: 768px)').matches) return;
                  onDragStart(event);
                }}
                role="presentation"
              >
                <img src={avatarSrc} alt="" />
                <div>
                  <strong>{name}</strong>
                  <span>Here to help!</span>
                </div>
              </div>
              <div className="cool-assistant-header-actions">
                <button
                  type="button"
                  className="cool-assistant-iconbtn"
                  aria-label="More options"
                  onClick={() => setMenuOpen((v) => !v)}
                >
                  <MoreHorizontal size={18} />
                </button>
                <button
                  type="button"
                  className="cool-assistant-iconbtn"
                  onClick={() => setIsOpen(false)}
                  aria-label={`Close ${name}`}
                >
                  <X size={18} />
                </button>
                {menuOpen && (
                  <div className="cool-assistant-menu">
                    <button
                      type="button"
                      onClick={() => {
                        setMessages([]);
                        setMenuOpen(false);
                      }}
                    >
                      New conversation
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="cool-assistant-messages" ref={listRef}>
              <div className="cool-assistant-row cool-assistant-row--bot">
                <img className="cool-assistant-avatar" src={avatarSrc} alt="" />
                <div>
                  <div className="cool-assistant-msg cool-assistant-msg--bot">
                    {welcome}
                  </div>
                </div>
              </div>

              {messages.map((m, i) => {
                const isUser = m.role === 'user';
                const cardGroups = normalizeCardGroups(m.cards);
                const hasCards = cardGroups.length > 0;
                const body = hasCards ? stripCardOverlaps(m.content) : m.content;
                return (
                  <div
                    key={`${m.role}-${i}`}
                    className={`cool-assistant-row cool-assistant-row--${isUser ? 'user' : 'bot'}`}
                  >
                    {!isUser && (
                      <img className="cool-assistant-avatar" src={avatarSrc} alt="" />
                    )}
                    <div className="cool-assistant-row-body">
                      {isUser ? (
                        <div className="cool-assistant-msg cool-assistant-msg--user">
                          {m.content}
                        </div>
                      ) : (
                        <div className={`cool-assistant-msg cool-assistant-msg--bot${hasCards ? ' cool-assistant-msg--feed' : ''}`}>
                          {body && <MessageText text={body} onOpenPath={openPath} />}
                          {cardGroups.map((group, gi) => (
                            <ListingCards
                              key={`${group.type || 'list'}-${gi}`}
                              cards={group}
                              onOpen={openListing}
                              isGuest={!user?.id}
                            />
                          ))}
                          {!body && !hasCards && <MessageText text={m.content} onOpenPath={openPath} />}
                        </div>
                      )}
                      <div className={`cool-assistant-meta cool-assistant-meta--${isUser ? 'user' : 'bot'}`}>
                        <span>{formatTime(m.at)}</span>
                        {isUser && <CheckCheck size={14} />}
                      </div>
                    </div>
                  </div>
                );
              })}

              {sending && (
                <div className="cool-assistant-row cool-assistant-row--bot">
                  <img className="cool-assistant-avatar" src={avatarSrc} alt="" />
                  <div className="cool-assistant-msg cool-assistant-msg--bot cool-assistant-typing">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}

              {messages.length === 0 && suggestions.length > 0 && (
                <div className="cool-assistant-suggestions">
                  {suggestions.map((s) => (
                    <button key={s} type="button" onClick={() => sendMessage(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form className="cool-assistant-composer" onSubmit={onSubmit}>
              <button
                type="button"
                className="cool-assistant-attach"
                aria-label="Attach a file"
                title="Attachments coming soon"
              >
                <Paperclip size={18} />
              </button>
              <div className="cool-assistant-inputwrap">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about jobs, events, news..."
                  maxLength={2000}
                  disabled={sending}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="sentences"
                  enterKeyHint="send"
                  inputMode="text"
                />
                <button
                  type="button"
                  className="cool-assistant-emoji"
                  aria-label="Insert emoji"
                  onClick={insertEmoji}
                >
                  <Smile size={18} />
                </button>
              </div>
              <button
                type="submit"
                className="cool-assistant-send"
                disabled={sending || !input.trim()}
              >
                <Send size={16} />
                <span className="cool-assistant-send-label">Send</span>
              </button>
            </form>
            <p className="cool-assistant-disclaimer">
              {name} can make mistakes. Please verify important information.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
